/**
 * Etecplus bundle customiser: <etec-bundle-builder>.
 * Loaded with `defer` by sections/etec-bundle-builder.liquid, after assets/etec-parts.js.
 *
 * Each slot holds one part (or none, if the shopper removed an optional slot). The bundle discount
 * applies when at least data-min parts are chosen. Swaps come from the slot's collection and are
 * filtered with the PC builder's compatibility check (EtecParts.checkProduct).
 *
 * Share links: ?build=<slot>,<slot>,… where each slot is "" (our pick), "x" (removed) or
 * "<product handle>:<variant id>".
 */
(() => {
  if (customElements.get('etec-bundle-builder')) return;

  const PARTS = window.EtecParts;
  const SHOW_STEP = 40;
  const MESSAGE_MS = 6000;
  const STORAGE_PREFIX = 'etec:bundle:';

  const { el, moneyText, sizedImage, rootUrl, parseTags, checkProduct, evaluateBuild } = PARTS;

  const absolute = (src) => (src && src.startsWith('//') ? `https:${src}` : src || null);

  /* Line icons for the slot buttons: the same Lucide paths as snippets/etec-icon.liquid. */
  const ICONS = {
    swap: ['M8 3 4 7l4 4', 'M4 7h16', 'm16 21 4-4-4-4', 'M20 17H4'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6', 'M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2', 'M10 11v6', 'M14 11v6'],
    plus: ['M5 12h14', 'M12 5v14'],
  };
  const icon = (name) => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'etec-icon');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    ICONS[name].forEach((d) => {
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', d);
      svg.append(path);
    });
    return svg;
  };
  const actionButton = (variant, iconName, label, ariaLabel) => {
    const button = el(
      'button',
      { type: 'button', class: `etec-bb__action etec-bb__action--${variant}`, 'aria-label': ariaLabel },
      icon(iconName),
      label ? el('span', { text: label }) : null
    );
    if (!label) button.title = ariaLabel;
    return button;
  };

  /** Products from Liquid or /products/<handle>.js (prices in cents) into the EtecParts product shape. */
  const fromCents = (raw) => {
    if (!raw) return null;
    const variants = (raw.variants || []).map((variant) => ({
      id: variant.id,
      title: variant.title,
      sku: '',
      price: Number(variant.price) || 0,
      compareAt: variant.compare_at_price ? Number(variant.compare_at_price) : null,
      available: Boolean(variant.available),
      image: variant.featured_image ? absolute(variant.featured_image.src || variant.featured_image) : null,
    }));
    const image = absolute(raw.featured_image || (raw.images && raw.images[0]) || null);
    const prices = variants.filter((variant) => variant.available).map((variant) => variant.price);
    return {
      id: raw.id,
      title: raw.title || '',
      handle: raw.handle,
      vendor: raw.vendor || '',
      url: `${rootUrl()}products/${raw.handle}`,
      image: typeof image === 'string' ? image : null,
      variants,
      available: variants.some((variant) => variant.available),
      sortPrice: prices.length ? Math.min(...prices) : variants.length ? variants[0].price : 0,
      data: parseTags(raw.tags),
      search: [raw.title, raw.vendor].join(' ').toLowerCase(),
    };
  };

  const firstVariant = (product) =>
    (product && (product.variants.find((variant) => variant.available) || product.variants[0])) || null;

  const storage = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        if (value == null) window.localStorage.removeItem(key);
        else window.localStorage.setItem(key, value);
      } catch (error) {
        /* private mode or blocked storage: the build just isn't remembered */
      }
    },
  };

  class EtecBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      let data = { slots: [] };
      try {
        data = JSON.parse(this.querySelector('[data-bb-data]').textContent);
      } catch (error) {
        data = { slots: [] };
      }

      this.format = this.dataset.moneyFormat || '${{amount}}';
      this.code = (this.dataset.code || '').trim();
      this.type = this.dataset.type === 'amount' ? 'amount' : 'percent';
      this.value = parseFloat(this.dataset.value) || 0;
      this.min = parseInt(this.dataset.min, 10) || 2;
      this.live = this.dataset.status === 'live';
      this.storageKey = `${STORAGE_PREFIX}${this.dataset.handle || 'bundle'}`;

      this.slots = (data.slots || []).map((slot, index) => {
        const product = fromCents(slot.product);
        const variant = firstVariant(product);
        return {
          index,
          label: slot.label || 'Part',
          role: slot.role || 'other',
          required: slot.required !== false,
          collection: slot.collection || null,
          pick: product,
          pickVariant: variant ? variant.id : null,
          product,
          variantId: variant ? variant.id : null,
          removed: !product,
          options: null,
        };
      });

      this.list = this.querySelector('[data-bb-slots]');
      this.picker = this.querySelector('[data-bb-picker]');
      this.optionsList = this.querySelector('[data-bb-options]');
      this.search = this.querySelector('[data-bb-search]');
      this.showAll = this.querySelector('[data-bb-show-all]');
      this.addButton = this.querySelector('[data-bb-add]');
      this.message = this.querySelector('[data-bb-message]');

      this.addButton.addEventListener('click', () => this.addToCart());
      this.querySelector('[data-bb-share]').addEventListener('click', () => this.share());
      this.querySelector('[data-bb-close]').addEventListener('click', () => this.closePicker());
      this.picker.addEventListener('click', (event) => {
        if (event.target === this.picker) this.closePicker();
      });
      this.picker.addEventListener('close', () => {
        if (this.returnFocus) this.returnFocus.focus();
      });
      this.search.addEventListener('input', () => {
        this.shown = SHOW_STEP;
        this.renderOptions();
      });
      this.showAll.addEventListener('change', () => this.renderOptions());
      this.addEventListener('etec:countdown-end', () => {
        this.live = false;
        this.update();
      });

      this.render();
      this.restore();
    }

    /* ---------- State ------------------------------------------------------------ */

    chosen() {
      return this.slots.filter((slot) => !slot.removed && slot.product);
    }

    variantOf(slot) {
      return slot.product ? slot.product.variants.find((variant) => variant.id === slot.variantId) || null : null;
    }

    parts(except) {
      return this.chosen()
        .filter((slot) => slot !== except)
        .map((slot) => ({ role: slot.role, title: slot.product.title, data: slot.product.data, qty: 1 }));
    }

    totals() {
      const chosen = this.chosen();
      const total = chosen.reduce((sum, slot) => sum + (this.variantOf(slot) ? this.variantOf(slot).price : 0), 0);
      const unlocked = this.live && Boolean(this.code) && chosen.length >= this.min;
      let saving = 0;
      if (unlocked) {
        saving = this.type === 'amount' ? Math.min(Math.round(this.value * 100), total) : Math.round((total * this.value) / 100);
      }
      return { count: chosen.length, total, saving, unlocked, soldOut: chosen.some((slot) => !this.variantOf(slot) || !this.variantOf(slot).available) };
    }

    /* ---------- Rendering ---------------------------------------------------------- */

    money(cents) {
      return moneyText(cents, this.format);
    }

    render() {
      this.list.replaceChildren(...this.slots.map((slot) => this.renderSlot(slot)));
      this.update();
    }

    renderSlot(slot) {
      const variant = this.variantOf(slot);
      const row = el('li', { class: `etec-bb__slot${slot.removed ? ' is-removed' : ''}`, data: { slot: String(slot.index) } });
      const image = slot.product && !slot.removed ? sizedImage((variant && variant.image) || slot.product.image, 160) : null;
      row.append(
        el(
          'div',
          { class: `etec-bb__thumb${image ? '' : ' etec-bb__thumb--empty'}`, 'aria-hidden': 'true' },
          // Never leave the tile empty: the theme's base styles hide empty divs, which collapses the grid column.
          image ? el('img', { src: image, alt: '', width: '80', height: '80', loading: 'lazy' }) : icon('plus')
        )
      );

      const info = el('div', { class: 'etec-bb__info' }, el('span', { class: 'etec-bb__slot-label', text: slot.label }));
      if (slot.removed || !slot.product) {
        info.append(el('span', { class: 'etec-bb__slot-empty', text: 'Not included' }));
      } else {
        // Built with el() so the optional chips (null when not shown) are skipped, not printed as "null".
        info.append(
          el(
            'span',
            { class: 'etec-bb__slot-main' },
            el('a', { class: 'etec-bb__slot-name', href: slot.product.url, target: '_blank', rel: 'noopener', text: slot.product.title }),
            variant && slot.product.variants.length > 1 ? el('span', { class: 'etec-bb__slot-variant', text: variant.title }) : null,
            slot.product === slot.pick && slot.variantId === slot.pickVariant ? el('span', { class: 'etec-bb__chip', text: 'Our pick' }) : null,
            variant && !variant.available ? el('span', { class: 'etec-bb__chip etec-bb__chip--bad', text: 'Sold out' }) : null
          )
        );
        const fit = this.fitOf(slot);
        if (fit === 'incompatible') info.append(el('span', { class: 'etec-bb__chip etec-bb__chip--bad', text: "Doesn't fit the rest" }));
      }
      row.append(info);

      row.append(
        el('span', { class: 'etec-bb__slot-price', text: !slot.removed && variant ? this.money(variant.price) : '' })
      );

      const actions = el('div', { class: 'etec-bb__actions' });
      if (slot.collection && !slot.removed) {
        const change = actionButton('change', 'swap', 'Change', `Change ${slot.label}`);
        change.addEventListener('click', () => this.openPicker(slot, change));
        actions.append(change);
      }
      if (!slot.required && !slot.removed) {
        const remove = actionButton('remove', 'trash', null, `Remove ${slot.label}`);
        remove.addEventListener('click', () => this.setRemoved(slot, true));
        actions.append(remove);
      }
      if (slot.removed) {
        const back = actionButton('add', 'plus', 'Add back', `Add ${slot.label} back`);
        back.addEventListener('click', () => this.setRemoved(slot, false));
        actions.append(back);
      }
      row.append(actions);
      return row;
    }

    fitOf(slot) {
      if (!slot.product || slot.role === 'other') return 'none';
      return checkProduct({ role: slot.role, data: slot.product.data }, this.parts(slot)).status;
    }

    update() {
      const { count, total, saving, unlocked, soldOut } = this.totals();
      this.querySelector('[data-bb-count]').textContent = String(count);
      this.querySelector('[data-bb-parts]').textContent = this.money(total);
      this.querySelector('[data-bb-discount]').textContent = saving ? `−${this.money(saving)}` : this.money(0);
      this.querySelector('[data-bb-total]').textContent = this.money(total - saving);

      const unlock = this.querySelector('[data-bb-unlock]');
      const need = this.min - count;
      if (!this.live) {
        unlock.textContent = 'This deal has ended. You can still buy these parts at their normal price.';
      } else if (need > 0) {
        unlock.textContent = `Add ${need} more ${need === 1 ? 'part' : 'parts'} to get the bundle discount.`;
      } else {
        unlock.textContent = `Bundle discount unlocked: you save ${this.money(saving)}.`;
      }
      unlock.classList.toggle('is-unlocked', unlocked);

      const fitNode = this.querySelector('[data-bb-fit]');
      const build = evaluateBuild(this.parts(null));
      if (build.issues.length) {
        const issue = build.issues[0];
        fitNode.textContent = `Check your parts: ${issue.a} and ${issue.b} don't match (${issue.key}).`;
        fitNode.hidden = false;
      } else if (build.warnings.length) {
        const warning = build.warnings[0];
        fitNode.textContent = `${warning.part} may be too small for this build. We recommend ${warning.recommended}W or more.`;
        fitNode.hidden = false;
      } else {
        fitNode.hidden = true;
      }

      this.addButton.disabled = count === 0 || soldOut;
      this.querySelector('[data-bb-add-label]').textContent = soldOut
        ? 'Change or remove the sold-out part'
        : unlocked
          ? `Add bundle to cart · ${this.money(total - saving)}`
          : 'Add parts to cart';
      storage.set(this.storageKey, this.encode());
    }

    setRemoved(slot, removed) {
      slot.removed = removed;
      if (!removed && !slot.product) {
        slot.product = slot.pick;
        slot.variantId = slot.pickVariant;
      }
      this.render();
      const row = this.list.querySelector(`[data-slot="${slot.index}"] .etec-bb__action`);
      if (row) row.focus();
    }

    choose(slot, product, variantId) {
      slot.product = product;
      slot.variantId = variantId;
      slot.removed = false;
      this.render();
      // render() replaced the row, so send focus back to the new Change button when the picker closes.
      this.returnFocus = this.list.querySelector(`[data-slot="${slot.index}"] .etec-bb__action`) || this.returnFocus;
      this.closePicker();
    }

    /* ---------- Picker --------------------------------------------------------------- */

    async openPicker(slot, trigger) {
      this.activeSlot = slot;
      this.returnFocus = trigger;
      this.shown = SHOW_STEP;
      this.search.value = '';
      this.showAll.checked = false;
      this.querySelector('[data-bb-picker-title]').textContent = `Choose ${slot.label.toLowerCase()}`;
      if (typeof this.picker.showModal === 'function') this.picker.showModal();
      else this.picker.setAttribute('open', '');
      this.search.focus();

      if (!slot.options) {
        this.optionsList.replaceChildren(el('li', { class: 'etec-bb__state', text: 'Loading parts…' }));
        try {
          slot.options = await PARTS.fetchCollection(slot.collection);
        } catch (error) {
          slot.options = null;
          this.optionsList.replaceChildren(el('li', { class: 'etec-bb__state', text: "Couldn't load parts. Close this and try again." }));
          return;
        }
      }
      if (this.activeSlot === slot) this.renderOptions();
    }

    closePicker() {
      if (this.picker.open) {
        if (typeof this.picker.close === 'function') this.picker.close();
        else this.picker.removeAttribute('open');
      }
      this.activeSlot = null;
    }

    renderOptions() {
      const slot = this.activeSlot;
      if (!slot || !slot.options) return;
      const query = this.search.value.trim().toLowerCase();
      const others = this.parts(slot);
      let hidden = 0;

      const rows = [];
      slot.options.forEach((product) => {
        if (query && !query.split(/\s+/).every((word) => product.search.includes(word))) return;
        const fit = slot.role === 'other' ? 'none' : checkProduct({ role: slot.role, data: product.data }, others).status;
        if (fit === 'incompatible' && !this.showAll.checked) {
          hidden += 1;
          return;
        }
        product.variants.forEach((variant) => rows.push({ product, variant, fit }));
      });

      rows.sort((a, b) => Number(b.variant.available) - Number(a.variant.available) || a.variant.price - b.variant.price);

      if (!rows.length) {
        this.optionsList.replaceChildren(
          el('li', { class: 'etec-bb__state', text: hidden ? 'No parts that fit. Tick "Show parts that may not fit" to see all.' : 'No parts match your search.' })
        );
        return;
      }

      const items = rows.slice(0, this.shown).map((row) => this.renderOption(slot, row));
      if (rows.length > this.shown) {
        const more = el('button', { type: 'button', class: 'etec-bb__action', text: `Show more (${rows.length - this.shown})` });
        more.addEventListener('click', () => {
          this.shown += SHOW_STEP;
          this.renderOptions();
        });
        items.push(el('li', { class: 'etec-bb__more' }, more));
      }
      if (hidden && !this.showAll.checked) {
        items.push(el('li', { class: 'etec-bb__state', text: `${hidden} ${hidden === 1 ? 'part is' : 'parts are'} hidden because they don't fit your other parts.` }));
      }
      this.optionsList.replaceChildren(...items);
    }

    renderOption(slot, { product, variant, fit }) {
      const current = slot.product && slot.product.id === product.id && slot.variantId === variant.id;
      const image = sizedImage(variant.image || product.image, 120);
      const fitLabel = { compatible: 'Fits', unknown: 'Check fit', warning: 'Check power', incompatible: "Doesn't fit" }[fit];
      const button = el(
        'button',
        {
          type: 'button',
          class: `etec-bb__option${current ? ' is-current' : ''}`,
          disabled: !variant.available || current,
          'aria-pressed': current ? 'true' : 'false',
        },
        el('span', { class: 'etec-bb__thumb etec-bb__thumb--small', 'aria-hidden': 'true' }, image ? el('img', { src: image, alt: '', width: '56', height: '56', loading: 'lazy' }) : null),
        el(
          'span',
          { class: 'etec-bb__option-info' },
          el('span', { class: 'etec-bb__option-name', text: product.title }),
          product.variants.length > 1 ? el('span', { class: 'etec-bb__slot-variant', text: variant.title }) : null,
          el(
            'span',
            { class: 'etec-bb__option-meta' },
            fitLabel ? el('span', { class: `etec-bb__chip etec-bb__chip--${fit}`, text: fitLabel }) : null,
            current ? el('span', { class: 'etec-bb__chip', text: 'In your bundle' }) : null,
            !variant.available ? el('span', { class: 'etec-bb__chip etec-bb__chip--bad', text: 'Sold out' }) : null
          )
        ),
        el('span', { class: 'etec-bb__option-price', text: this.money(variant.price) })
      );
      button.addEventListener('click', () => this.choose(slot, product, variant.id));
      return el('li', null, button);
    }

    /* ---------- Share and restore ------------------------------------------------------ */

    encode() {
      return this.slots
        .map((slot) => {
          if (slot.removed) return 'x';
          if (slot.product === slot.pick && slot.variantId === slot.pickVariant) return '';
          return `${slot.product.handle}:${slot.variantId}`;
        })
        .join(',');
    }

    async restore() {
      const params = new URLSearchParams(window.location.search);
      const text = params.has('build') ? params.get('build') : storage.get(this.storageKey);
      if (!text) return;
      const entries = text.split(',');
      if (entries.length !== this.slots.length) return;

      await Promise.all(
        entries.map(async (entry, index) => {
          const slot = this.slots[index];
          if (entry === 'x') {
            if (!slot.required) slot.removed = true;
            return;
          }
          const match = entry.match(/^([a-z0-9][a-z0-9-]*):(\d+)$/);
          if (!match) return;
          try {
            const response = await fetch(`${rootUrl()}products/${match[1]}.js`, { headers: { Accept: 'application/json' } });
            if (!response.ok) return;
            const product = fromCents(await response.json());
            const variant = product.variants.find((item) => item.id === Number(match[2]));
            if (variant) {
              slot.product = product;
              slot.variantId = variant.id;
              slot.removed = false;
            }
          } catch (error) {
            /* keep our pick for this slot */
          }
        })
      );
      this.render();
    }

    async share() {
      const url = new URL(window.location.href);
      const build = this.encode();
      if (build.replace(/,/g, '')) url.searchParams.set('build', build);
      else url.searchParams.delete('build');
      const link = url.toString();
      try {
        await navigator.clipboard.writeText(link);
        this.say('Link copied. Anyone with it sees this exact build.');
      } catch (error) {
        this.say(`Copy this link: ${link}`);
      }
    }

    say(text, tone) {
      this.message.textContent = text || '';
      this.message.dataset.tone = tone || '';
      this.message.hidden = !text;
      window.clearTimeout(this.messageTimer);
      if (text && tone !== 'error') this.messageTimer = window.setTimeout(() => this.say(''), MESSAGE_MS);
    }

    /* ---------- Cart --------------------------------------------------------------------- */

    async addToCart() {
      const { unlocked, soldOut } = this.totals();
      const chosen = this.chosen();
      if (!chosen.length || soldOut) return;

      const title = this.dataset.title || 'Bundle';
      const items = chosen.map((slot) => ({ id: slot.variantId, quantity: 1, properties: { _Bundle: title } }));
      const sections = window.EtecCart.sections();
      this.addButton.disabled = true;
      this.classList.add('is-busy');
      this.say('');

      try {
        let data = await window.EtecCart.request('cart/add.js', unlocked || !sections ? { items } : Object.assign({ items }, sections));
        if (unlocked) {
          const result = await window.EtecCart.applyCode(this.code, sections);
          data = result.data;
          if (!result.applied) {
            this.say("Parts added. The bundle discount couldn't be applied, so please contact us and we'll sort it out.", 'error');
          }
        }
        if (!window.EtecCart.render(data, this.addButton)) {
          window.location.href = window.EtecCart.url();
          return;
        }
      } catch (error) {
        this.say(error.message || 'Something went wrong adding these parts. Please try again.', 'error');
      } finally {
        this.classList.remove('is-busy');
        this.update();
      }
    }
  }

  customElements.define('etec-bundle-builder', EtecBundleBuilder);
})();
